import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  BiX,
  BiTable,
  BiExpand,
  BiCollapse,
  BiSearch,
  BiData,
  BiTrendingUp,
  BiTrendingDown,
  BiMinus,
  BiBarChartAlt2,
  BiLineChart,
  BiPieChartAlt2,
  BiLinkExternal,
  BiGridAlt,
  BiListUl,
  BiChevronDown,
  BiChevronUp,
  BiWrench,
  BiInfoCircle,
  BiCalendar,
} from 'react-icons/bi'
import { FiLayout, FiCheckCircle } from 'react-icons/fi'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import AgentResultsDashboard from './AgentResultsDashboard'
import { detectAgentDataSource, getAgentDataSourceDisplayName } from '../utils/agentDataSource'

// Tool descriptions with examples for tooltips
const toolDescriptions: Record<string, { description: string; example: string }> = {
  search: {
    description: 'Search across orders, customers, products, and more using natural language queries.',
    example: '"Show me orders from last week" or "Find customers who spent over $500"',
  },
  fetch: {
    description: 'Retrieve detailed information about a specific record by ID.',
    example: '"Get order #12345 details" or "Fetch customer info for john@example.com"',
  },
  insure_shipment: {
    description: 'Add shipping insurance to a specific shipment.',
    example: '"Insure shipment for order #12345" or "Add insurance to tracking ABC123"',
  },
  void_insurance: {
    description: 'Cancel shipping insurance on a shipment.',
    example: '"Void insurance for order #12345" or "Cancel insurance on shipment ABC123"',
  },
  update_shipping_address: {
    description: 'Update the shipping address for an order.',
    example: '"Change shipping address for order #12345 to 123 Main St"',
  },
  update_auto_insurance_settings: {
    description: 'Configure automatic insurance settings for future shipments.',
    example: '"Enable auto-insurance for orders over $100" or "Turn off automatic insurance"',
  },
  update_carrier_first_hundred_coverage: {
    description: 'Set the first $100 coverage preference for a carrier.',
    example: '"Enable first $100 coverage for UPS" or "Disable carrier coverage for FedEx"',
  },
  update_carrier_insurance_rule: {
    description: 'Configure insurance rules for specific carriers.',
    example: '"Set UPS insurance threshold to $200" or "Update FedEx insurance rules"',
  },
  get_current_shipping_settings: {
    description: 'View your current shipping and insurance configuration.',
    example: '"Show my shipping settings" or "What are my current insurance rules?"',
  },
  export_to_email: {
    description: 'Export report data and send it to an email address.',
    example: '"Email me the sales report" or "Send last month\'s orders to team@company.com"',
  },
  export_to_google_drive: {
    description: 'Export report data directly to your Google Drive.',
    example: '"Save this report to my Google Drive" or "Export orders to Drive"',
  },
  export_to_csv: {
    description: 'Download report data as a CSV file.',
    example: '"Download orders as CSV" or "Export customer list to spreadsheet"',
  },
  get_traffic_data: {
    description: 'Retrieve website traffic and visitor analytics.',
    example: '"Show traffic for last 30 days" or "Get visitor data by source"',
  },
  get_traffic_metrics: {
    description: 'Get key traffic metrics like sessions, bounce rate, and conversions.',
    example: '"What\'s my conversion rate?" or "Show bounce rate trends"',
  },
  get_ads_performance: {
    description: 'View advertising performance across Google Ads, Facebook, etc.',
    example: '"How are my Google Ads performing?" or "Show ad spend vs revenue"',
  },
  get_traffic_summary: {
    description: 'Get a high-level overview of traffic performance.',
    example: '"Give me a traffic summary" or "Overview of website visitors"',
  },
  // Google Ads tools
  get_google_ads_accounts: {
    description:
      'Lists accessible Google Ads customers per OAuth link. Omit includeManagerDiscovery (or false) = fast, no MCC probes. Pass includeManagerDiscovery: true only when you need isManagerAccount / managerCustomerIds / MCC names. For “accounts under [email]” use list_google_ads_client_accounts.',
    example: '"Which customer IDs are linked?" → omit flag; "Which account is my MCC?" → includeManagerDiscovery true',
  },
  list_google_ads_client_accounts: {
    description:
      'List enabled client accounts under an MCC. For a named manager: get_google_ads_accounts with includeManagerDiscovery true, match descriptiveName, then pass connectedGoogleEmail + managerCustomerId. If the user gave the numeric MCC ID, pass email + managerCustomerId only. Email alone auto-detects every MCC on that login.',
    example:
      '"List clients under North Digital MCC via chris@north.digital" (get_google_ads_accounts with discovery → list with email + manager id) or "Clients under manager 1234567890 for example@email.com"',
  },
  search_google_ads: {
    description: 'Search Google Ads campaigns, ad groups, keywords, and performance data.',
    example: '"Show my top performing campaigns" or "Find ads with CTR below 1%"',
  },
  modify_google_ads: {
    description: 'Update Google Ads campaigns, budgets, bids, or pause/enable ads.',
    example: '"Increase budget for Campaign X by 20%" or "Pause underperforming ads"',
  },
  create_google_ads_pmax_campaign: {
    description: 'Create a Google Ads Performance Max campaign. Requires images attached in chat (not handled by the tool): one landscape 1.91:1 min 600×314 px (marketing), one square 1:1 min 300×300 px (square marketing and logo).',
    example: '"Create a Performance Max campaign for my brand" or "Set up a PMax campaign with these images"',
  },
  create_google_ads_conversion: {
    description: 'Create a new conversion action in Google Ads (e.g. website purchase, lead, offline upload).',
    example: '"Add a purchase conversion for my site" or "Create an offline conversion for uploads"',
  },
  modify_google_ads_conversion: {
    description: 'Update an existing conversion action: rename, pause/enable, category, default value, or click/ view-through lookback windows.',
    example: '"Pause conversion X" or "Change the default conversion value to $25"',
  },
  remove_google_ads_conversion: {
    description: 'Remove a conversion action from Google Ads using the API remove operation (when the user wants to delete the action, not just pause it).',
    example: '"Delete the old purchase conversion" or "Remove conversion action 12345678"',
  },
  // Facebook Ads tools
  get_facebook_ads_accounts: {
    description: 'List all connected Facebook/Meta Ads accounts.',
    example: '"Show my Facebook ad accounts" or "List Meta advertising accounts"',
  },
  get_openai_ads_accounts: {
    description: 'List the connected OpenAI Ads (ChatGPT Ads) account.',
    example: '"Show my OpenAI Ads account" or "List ChatGPT Ads campaigns"',
  },
  search_openai_ads: {
    description: 'List OpenAI Ads campaigns, ad groups, ads, or custom audiences.',
    example: '"List my ChatGPT Ads campaigns" or "Show OpenAI ad groups"',
  },
  search_facebook_ads: {
    description: 'Search Facebook Ads campaigns, ad sets, and performance metrics.',
    example: '"Show Facebook ads spend this month" or "Find best performing ad sets"',
  },
  modify_facebook_ads: {
    description: 'Update Facebook Ads campaigns, budgets, or targeting settings.',
    example: '"Increase Facebook ad budget" or "Pause ad set X"',
  },
  // Klaviyo tools
  get_klaviyo_accounts: {
    description: 'List connected Klaviyo accounts for email marketing.',
    example: '"Show my Klaviyo accounts" or "Which email accounts are connected?"',
  },
  search_klaviyo: {
    description: 'Search Klaviyo campaigns, flows, lists, and email performance data.',
    example: '"Show email open rates" or "Find flows with low conversion"',
  },
  // TikTok tools
  get_tiktok_accounts: {
    description: 'List connected TikTok Ads accounts.',
    example: '"Show my TikTok ad accounts" or "List TikTok advertising accounts"',
  },
  search_tiktok: {
    description: 'Search TikTok Ads campaigns and performance data.',
    example: '"Show TikTok ad spend" or "Find top TikTok campaigns"',
  },
  // Pinterest tools
  get_pinterest_accounts: {
    description: 'List connected Pinterest Ads accounts.',
    example: '"Show my Pinterest ad accounts" or "List Pinterest advertising accounts"',
  },
  search_pinterest: {
    description: 'Search Pinterest Ads campaigns and performance metrics.',
    example: '"Show Pinterest ad performance" or "Find Pinterest campaigns"',
  },
  // Mailchimp tools
  get_mailchimp_accounts: {
    description: 'List connected Mailchimp accounts for email marketing.',
    example: '"Show my Mailchimp accounts" or "Which Mailchimp accounts are connected?"',
  },
  search_mailchimp: {
    description: 'Search Mailchimp campaigns, audiences, and email performance.',
    example: '"Show recent email campaigns" or "Find campaigns with high open rates"',
  },
  create_mailchimp_list: {
    description: 'Create a new Mailchimp audience/list.',
    example: '"Create a test audience" or "Set up a new mailing list"',
  },
  create_mailchimp_campaign: {
    description: 'Create a new Mailchimp email campaign.',
    example: '"Create a new email campaign" or "Set up a newsletter"',
  },
  update_mailchimp_campaign: {
    description: 'Update a Mailchimp campaign\'s settings or content.',
    example: '"Update the subject line" or "Change campaign content"',
  },
  send_mailchimp_campaign: {
    description: 'Send or schedule a Mailchimp campaign.',
    example: '"Send the campaign now" or "Schedule campaign for tomorrow"',
  },
  replicate_mailchimp_campaign: {
    description: 'Copy/replicate a Mailchimp campaign.',
    example: '"Copy that campaign" or "Replicate the sent campaign"',
  },
  delete_mailchimp_campaign: {
    description: 'Delete a Mailchimp campaign.',
    example: '"Delete the draft campaign" or "Remove that campaign"',
  },
  create_mailchimp_member: {
    description: 'Add or update a subscriber in a Mailchimp list.',
    example: '"Add a new subscriber" or "Update member info"',
  },
  add_members_to_mailchimp_list: {
    description: 'Batch add or update members in a Mailchimp list.',
    example: '"Add these contacts to the list" or "Import subscribers"',
  },
  tag_mailchimp_member: {
    description: 'Add or remove tags on a Mailchimp subscriber.',
    example: '"Tag this member as VIP" or "Remove the inactive tag"',
  },
  get_mailchimp_content: {
    description: 'Get HTML content and URLs from a Mailchimp campaign or automation email.',
    example: '"Show me URLs in campaign X" or "Get content from automation email"',
  },
  update_mailchimp_content: {
    description: 'Find and replace a URL in Mailchimp campaign or automation email HTML.',
    example: '"Replace old-link.com with new-link.com in campaign X"',
  },
  get_mailchimp_automation_emails: {
    description: 'List all emails in a Mailchimp automation/drip sequence.',
    example: '"Show emails in automation X" or "List the drip sequence emails"',
  },
  get_klaviyo_template: {
    description: 'Get HTML content and URLs from a Klaviyo email template.',
    example: '"Show me URLs in template X" or "Get template content"',
  },
  update_klaviyo_template: {
    description: 'Find and replace a URL in a Klaviyo template. Warning: affects all campaigns/flows using it.',
    example: '"Replace old-link.com with new-link.com in template X"',
  },
  get_klaviyo_flow_messages: {
    description: 'List all messages in a Klaviyo flow with their template IDs.',
    example: '"Show messages in flow X" or "What templates are used in this flow?"',
  },
  // Stripe tools
  get_stripe_info: {
    description: 'Check Stripe billing connection status and account summary.',
    example: '"Is Stripe connected?" or "Show Stripe account info"',
  },
  search_stripe: {
    description: 'Search Stripe customers, subscriptions, charges, invoices, and refunds.',
    example: '"Find customer by email" or "Show cancelled subscriptions" or "List recent refunds"',
  },
  // Inventory tools
  get_inventory_products: {
    description: 'Retrieve product inventory levels and stock information.',
    example: '"Show low stock products" or "What products need reordering?"',
  },
  get_inventory_variants: {
    description: 'Get inventory details for product variants (sizes, colors, etc.).',
    example: '"Show stock levels by size" or "Which variants are out of stock?"',
  },
  get_inventory_brands: {
    description: 'View inventory organized by brand.',
    example: '"Show inventory by brand" or "Which brands have low stock?"',
  },
  get_inventory_summary: {
    description: 'Get an overview of total inventory value and stock levels.',
    example: '"Give me an inventory summary" or "What\'s my total stock value?"',
  },
  // eBay tools
  ebay_create_product: {
    description: 'Create a new product listing on eBay with title, description, price, and images.',
    example: '"Create an eBay listing for my new product" or "List this item on eBay"',
  },
  ebay_create_listing: {
    description: 'Create a new eBay listing with offer details like price, quantity, and shipping.',
    example: '"List this product on eBay at $29.99" or "Create an eBay offer for my item"',
  },
  ebay_edit_listing: {
    description: 'Update an existing eBay listing\'s price, quantity, description, or other details.',
    example: '"Change eBay listing price to $24.99" or "Update my eBay listing description"',
  },
  ebay_delete_listing: {
    description: 'Remove a listing from eBay.',
    example: '"Delete my eBay listing" or "Remove this product from eBay"',
  },
  ebay_get_inventory_item: {
    description: 'Retrieve details for a specific eBay inventory item by SKU.',
    example: '"Get eBay inventory for SKU ABC123" or "Show my eBay item details"',
  },
  ebay_get_offer: {
    description: 'Get details of a specific eBay offer by offer ID.',
    example: '"Show eBay offer details" or "Get info on my eBay offer"',
  },
  ebay_get_offers: {
    description: 'List all offers for a specific eBay inventory item.',
    example: '"Show all offers for this eBay item" or "List my eBay offers"',
  },
  ebay_get_listing: {
    description: 'Retrieve a specific eBay listing with full details.',
    example: '"Show my eBay listing" or "Get details for eBay listing #12345"',
  },
  ebay_search_listings: {
    description: 'Search across your eBay listings by keyword, status, or category.',
    example: '"Show all active eBay listings" or "Find eBay listings for shoes"',
  },
  // Wix tools
  wix_create_product: {
    description: 'Create a new product in your Wix store with name, price, and details.',
    example: '"Add a new product to my Wix store" or "Create a Wix product listing"',
  },
  wix_edit_product: {
    description: 'Update an existing product in your Wix store.',
    example: '"Update my Wix product price" or "Change product description on Wix"',
  },
  wix_delete_product: {
    description: 'Remove a product from your Wix store.',
    example: '"Delete this product from Wix" or "Remove item from my Wix store"',
  },
  wix_get_product: {
    description: 'Retrieve details for a specific product in your Wix store.',
    example: '"Show Wix product details" or "Get info on my Wix product"',
  },
  wix_query_products: {
    description: 'Search and filter products in your Wix store.',
    example: '"Show all Wix products" or "Find products in my Wix store under $50"',
  },
  // Report tools
  get_orders_report: {
    description: 'Pre-built report of orders with revenue, order counts, and date range filters.',
    example: '"Show my orders report for last month" or "Get order stats for Q4"',
  },
  get_line_items_report: {
    description: 'Detailed line-item breakdown of orders with product-level sales data.',
    example: '"Show line item details for recent orders" or "Break down orders by product"',
  },
  get_charts_report: {
    description: 'Chart-ready time series data for orders, revenue, and key metrics.',
    example: '"Show revenue chart for last 30 days" or "Graph my order trends"',
  },
  get_trends_report: {
    description: 'Period-over-period trend analysis showing growth rates and changes.',
    example: '"How are my sales trending?" or "Show growth vs last month"',
  },
  get_year_comparison_report: {
    description: 'Year-over-year comparison of key business metrics.',
    example: '"Compare this year to last year" or "Show YoY revenue growth"',
  },
  get_customers_report: {
    description: 'Customer analytics including new vs returning, lifetime value, and segments.',
    example: '"Show customer report" or "How many new customers this month?"',
  },
  get_products_report: {
    description: 'Product performance report with sales, revenue, and rankings.',
    example: '"Show top products" or "Which products sell the most?"',
  },
  get_product_trends_report: {
    description: 'Product sales trends over time showing momentum and seasonality.',
    example: '"Show product sales trends" or "Which products are growing?"',
  },
  get_variants_report: {
    description: 'Variant-level performance data broken down by size, color, or option.',
    example: '"Show sales by variant" or "Which sizes sell best?"',
  },
  get_brands_report: {
    description: 'Brand-level sales and revenue breakdown.',
    example: '"Show sales by brand" or "Which brands perform best?"',
  },
  get_skus_report: {
    description: 'SKU-level inventory and sales performance data.',
    example: '"Show SKU performance" or "Which SKUs have the most sales?"',
  },
  get_custom_order_bases_report: {
    description: 'Custom report with flexible dimensions and metrics for advanced analysis.',
    example: '"Build a custom report by region and product" or "Show orders grouped by discount code"',
  },
  get_marketing_report: {
    description: 'Marketing performance across all channels with attribution and spend data.',
    example: '"Show my marketing report" or "How is marketing performing?"',
  },
  get_integrated_accounts: {
    description: 'List all connected platform integrations and their status.',
    example: '"What integrations do I have?" or "Show connected accounts"',
  },
  // MMM tools
  get_mmm_summary: {
    description: 'Get an overview of Marketing Mix Model results including channel attribution and overall model performance.',
    example: '"Show my MMM results" or "How is my marketing budget performing?"',
  },
  get_mmm_channels: {
    description: 'Get detailed per-channel MMM metrics including ROAS, contribution, and saturation curves.',
    example: '"Show MMM channel breakdown" or "Which channels have the best ROAS?"',
  },
  get_mmm_budget_scenarios: {
    description: 'Get pre-computed budget reallocation scenarios showing how to optimize spend across channels.',
    example: '"Show budget optimization scenarios" or "How should I reallocate my budget?"',
  },
  run_mmm_simulation: {
    description: 'Run a custom budget simulation to forecast revenue impact of spending changes.',
    example: '"What if I increase Google Ads budget by 20%?" or "Simulate $10K shift from Facebook to TikTok"',
  },
}

// Format tool name for display: snake_case → Title Case, strip common prefixes
const formatToolLabel = (tool: string): string => {
  return tool
    .replace(/^(get_|search_|run_|fetch_)/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

// Tooltip component for tool descriptions
const ToolTooltip: React.FC<{
  tool: string
  children: React.ReactNode
}> = ({ tool, children }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const tooltipRef = useRef<HTMLDivElement>(null)

  const info = toolDescriptions[tool]

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect()
    setPosition({ x: rect.left, y: rect.bottom + 4 })
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  if (!info) {
    return <>{children}</>
  }

  return (
    <div
      className="tw-relative tw-inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className="tw-fixed tw-z-50 tw-w-72 tw-p-3 tw-bg-gray-900 tw-text-white tw-rounded-lg tw-shadow-xl tw-text-xs"
          style={{ left: Math.min(position.x, window.innerWidth - 300), top: position.y }}
        >
          <div className="tw-font-semibold tw-text-blue-300 tw-mb-1 tw-flex tw-items-center tw-gap-1">
            <BiInfoCircle size={12} />
            {tool}
          </div>
          <div className="tw-text-gray-200 tw-mb-2">{info.description}</div>
          <div className="tw-border-t tw-border-gray-700 tw-pt-2 tw-mt-2">
            <div className="tw-text-gray-400 tw-text-[10px] tw-uppercase tw-tracking-wide tw-mb-1">Try asking:</div>
            <div className="tw-text-green-300 tw-italic">{info.example}</div>
          </div>
        </div>
      )}
    </div>
  )
}

// Data source icons as inline SVGs for integrations
const DataSourceIcons: Record<string, React.FC<{ size?: number; className?: string }>> = {
  google: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),
  google_analytics: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#F9AB00" d="M4 18h3v-7H4v7zm5 0h3V8H9v10zm5 0h3v-4h-3v4z" />
      <path fill="#E37400" d="M17 10h3v8h-3v-8z" />
    </svg>
  ),
  google_tag_manager: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#246FDB" d="M4 5h7v7H4V5zm9 0h7v4h-7V5zm0 6h7v8h-7v-8zM4 14h7v5H4v-5z" />
      <path fill="#34A853" d="M6 16h3v1.5H6V16z" />
    </svg>
  ),
  klaviyo: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#1A1A1A" d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l7.5 3.75L12 11.68 4.5 7.93 12 4.18zM4 9.07l7 3.5v7.36l-7-3.5V9.07zm9 10.86v-7.36l7-3.5v7.36l-7 3.5z"/>
    </svg>
  ),
  shipsurance: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#2563EB" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  ),
  shopify: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#96BF48" d="M15.337 3.415c-.03-.183-.148-.275-.275-.275-.122 0-2.474-.183-2.474-.183s-1.636-1.636-1.819-1.819c-.183-.183-.55-.122-.703-.092 0 0-.367.122-.947.306C8.783.555 8.447.098 7.835.037c-.916-.092-1.819.733-2.474 1.819-.519.855-1.053 1.941-1.191 2.765-.885.275-1.499.458-1.499.458-.458.122-.489.153-.55.58C2.06 6.054 0 23.5 0 23.5l12.474 2.283V3.843c-.03-.03-2.535-.458-2.535-.458s-.519 1.636-.672 3.89c-.489.153-.977.306-1.466.428 0-1.28-.183-3.462-.885-3.462-.703 0-1.28 1.758-1.636 3.432-.672.214-1.341.428-1.977.611 0 0 .336-2.657.306-2.932-.03-.275.092-.519.489-.611.275-.092 3.005-.916 3.005-.916s-.031-.214-.031-.55c-.031-.702-.55-1.282-1.099-1.312-.55-.031-1.007.367-1.343.764-.367.428-.764 1.129-.764 1.129l-2.443.733c.122-.764.458-1.941.977-2.796.977-1.605 2.322-1.697 2.932-1.007.61.703.427 1.988.427 2.443 0 .275.031.764.092 1.221 1.129-.336 2.535-.764 2.535-.764s.153-.855.336-1.819c.183-.916.519-2.046 1.465-2.046.55 0 .764.55.764 1.374 0 .977-.214 2.261-.214 3.157 0 0 2.657-.825 2.718-.855.214-.092.489-.061.55.183z"/>
    </svg>
  ),
  payhelm: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="10" fill="#3B82F6"/>
      <path fill="white" d="M8 8h4c1.1 0 2 .9 2 2v0c0 1.1-.9 2-2 2H8v4H6V8h2zm0 3h3.5c.28 0 .5-.22.5-.5v0c0-.28-.22-.5-.5-.5H8v1z"/>
      <path fill="white" d="M14 12h2v4h-2z"/>
    </svg>
  ),
  facebook: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  meta: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#0081FB" d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
    </svg>
  ),
  database: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  ),
}

interface AgentStep {
  iteration: number
  status: 'running' | 'completed' | 'failed'
  thought?: string
  tool?: string
  toolInput?: any
  toolOutput?: any
  timestamp?: number
}

interface AgentDataPanelProps {
  steps: AgentStep[]
  status: 'idle' | 'running' | 'completed' | 'failed'
  isExpanded: boolean
  onToggleExpand: () => void
  onClose: () => void
  availableTools?: string[]
  /** Second action in collapsed rail: open scheduled task panel from chat */
  onOpenScheduledTask?: () => void
  /** Show calendar shortcut below Data Explorer when collapsed (agent chat) */
  scheduledTaskShortcutVisible?: boolean
}

interface ParsedData {
  metrics: any[]
  rows: any[]
  title: string
  query?: string
  chartData?: ChartData | null
  reportUrl?: string
}

interface ChartData {
  type: 'bar' | 'line' | 'pie'
  title: string
  categories?: string[]
  series: { name: string; data: number[] }[]
  yAxisTitle?: string
}

/**
 * Check if a tool is a configuration/account tool that returns metadata, not analytics data.
 * These tools should NOT be charted - they show account information, not data to visualize.
 */
const isConfigurationTool = (toolName?: string): boolean => {
  if (!toolName) return false
  const name = toolName.toLowerCase().replace('mcp__payhelm__', '')

  // Account listing tools - return list of connected accounts, not analytics
  const accountTools = [
    'get_google_ads_accounts',
    'list_google_ads_client_accounts',
    'get_facebook_ads_accounts',
    'get_openai_ads_accounts',
    'get_tiktok_accounts',
    'get_pinterest_accounts',
    'get_klaviyo_accounts',
    'get_bing_ads_accounts',
    'get_connected_accounts',
    'get_account_info',
    'get_account_details',
  ]

  // Settings/configuration tools - return settings info, not analytics
  const settingsTools = [
    'get_account_settings',
    'get_tracking_settings',
    'get_conversion_settings',
    'get_user_settings',
    'get_integration_status',
    'get_current_shipping_settings',
  ]

  return accountTools.includes(name) || settingsTools.includes(name)
}

// Detect chart type from data structure
const detectChartType = (rows: any[]): 'bar' | 'line' | 'pie' | null => {
  // Minimum 2 rows required for any meaningful chart
  if (!rows || rows.length < 2) return null

  const firstRow = rows[0]
  const keys = Object.keys(firstRow)

  // Check for date/time fields (line chart)
  const dateFields = ['date', 'day', 'month', 'week', 'year', 'period', 'time', 'created_at', 'updated_at']
  const hasDateField = keys.some(k => dateFields.some(df => k.toLowerCase().includes(df)))

  // Check for numeric value fields
  const valueFields = ['total', 'revenue', 'amount', 'count', 'orders', 'sales', 'quantity', 'value', 'profit']
  const numericKeys = keys.filter(k => typeof firstRow[k] === 'number' || valueFields.some(vf => k.toLowerCase().includes(vf)))

  if (hasDateField && numericKeys.length > 0 && rows.length >= 3) {
    return 'line'
  }

  // If we have a name/label field and a value field -> bar chart
  const labelFields = ['name', 'title', 'product', 'category', 'sku', 'variant', 'customer', 'item']
  const hasLabelField = keys.some(k => labelFields.some(lf => k.toLowerCase().includes(lf)))

  if (hasLabelField && numericKeys.length > 0 && rows.length >= 2 && rows.length <= 15) {
    return 'bar'
  }

  // Small datasets with percentages or ratios -> pie chart
  if (rows.length >= 2 && rows.length <= 8) {
    const hasPercentage = keys.some(k => k.toLowerCase().includes('percent') || k.toLowerCase().includes('share'))
    if (hasPercentage) return 'pie'
  }

  // Default to bar for small datasets with numeric data
  if (numericKeys.length > 0 && rows.length >= 2 && rows.length <= 20) {
    return 'bar'
  }

  return null
}

// Generate chart data from rows
const generateChartData = (rows: any[], query?: string): ChartData | null => {
  if (!rows || rows.length === 0) return null

  const chartType = detectChartType(rows)
  if (!chartType) return null

  const firstRow = rows[0]
  const keys = Object.keys(firstRow)

  // Find label and value fields
  const labelFields = ['name', 'title', 'product', 'category', 'sku', 'variant', 'customer', 'item', 'date', 'day', 'month', 'week', 'period']
  const valueFields = ['total', 'revenue', 'amount', 'count', 'orders', 'sales', 'quantity', 'value', 'profit', 'price']

  let labelKey = keys.find(k => labelFields.some(lf => k.toLowerCase().includes(lf))) || keys[0]
  let valueKey = keys.find(k => {
    const isNumeric = typeof firstRow[k] === 'number'
    const isValueField = valueFields.some(vf => k.toLowerCase().includes(vf))
    return isNumeric && isValueField
  })

  // If no explicit value field, find any numeric field
  if (!valueKey) {
    valueKey = keys.find(k => typeof firstRow[k] === 'number' && k !== labelKey)
  }

  if (!valueKey) return null

  // Extract categories and values
  const categories = rows.map(row => {
    const val = row[labelKey]
    if (typeof val === 'string') return val.length > 25 ? val.slice(0, 22) + '...' : val
    return String(val)
  })

  const values = rows.map(row => Number(row[valueKey]) || 0)

  // Determine chart title
  let title = query ? query.slice(0, 50) : `${formatLabel(valueKey)} by ${formatLabel(labelKey)}`

  // Format y-axis title
  const yAxisTitle = formatLabel(valueKey)

  if (chartType === 'pie') {
    return {
      type: 'pie',
      title,
      series: [{
        name: yAxisTitle,
        data: categories.map((cat, i) => ({ name: cat, y: values[i] })) as any,
      }],
    }
  }

  return {
    type: chartType,
    title,
    categories,
    series: [{ name: yAxisTitle, data: values }],
    yAxisTitle,
  }
}

// Extract a clean PayHelm report URL from a string that may contain markdown or other text
const extractPayhelmUrl = (str: string): string | undefined => {
  const match = str.match(/https?:\/\/[^\s"'<>()]+payhelm\.com\/reports\/[^\s"'<>()]+/i)
  return match ? match[0] : undefined
}

// Extract report URL from data - searches deeply for any PayHelm report URL
const extractReportUrl = (data: any): string | undefined => {
  if (!data) return undefined

  if (typeof data === 'string') return extractPayhelmUrl(data)
  if (typeof data !== 'object') return undefined

  // Check results array items (MCP returns { results: [{ text, url, ... }] })
  if (data.results && Array.isArray(data.results)) {
    for (const result of data.results) {
      if (!result || typeof result !== 'object') continue
      for (const field of ['text', 'url', 'report', 'reportUrl', 'report_url', 'link']) {
        if (result[field] && typeof result[field] === 'string') {
          const url = extractPayhelmUrl(result[field])
          if (url) return url
        }
      }
    }
  }

  // Check direct fields
  const urlFields = ['report', 'reportUrl', 'reportLink', 'report_url', 'report_link', 'url', 'link', 'viewUrl', 'view_url']
  for (const field of urlFields) {
    const value = data[field]
    if (value && typeof value === 'string') {
      const url = extractPayhelmUrl(value)
      if (url) return url
    }
  }

  return undefined
}

// Extract meaningful data from tool output
const parseToolOutput = (output: any, toolInput?: any): ParsedData => {
  if (!output) return { metrics: [], rows: [], title: 'No Data', chartData: null }

  let data = output
  let title = 'Search Results'
  let query = toolInput?.query || ''

  // Handle string outputs (try to parse JSON)
  if (typeof output === 'string') {
    try {
      data = JSON.parse(output)
    } catch {
      return { metrics: [], rows: [], title: 'Response', query, chartData: null }
    }
  }

  // Extract report URL
  const reportUrl = extractReportUrl(data)

  // Extract query if present
  if (data.query) {
    query = data.query
    title = query
  }

  let rows: any[] = []

  // Handle array of results
  if (Array.isArray(data)) {
    rows = data.slice(0, 50)
  }
  // Handle object with results array
  else if (data.results && Array.isArray(data.results)) {
    rows = data.results.slice(0, 50)
    title = data.query || title
    query = data.query || query
  }
  // Handle object with data array
  else if (data.data && Array.isArray(data.data)) {
    rows = data.data.slice(0, 50)
    title = data.title || title
  }
  // Handle single object result
  else if (typeof data === 'object' && !Array.isArray(data)) {
    rows = [data]
  }

  const metrics = extractMetrics(rows, data)
  const chartData = generateChartData(rows, query)

  return { metrics, rows, title, query, chartData, reportUrl }
}

// Extract metrics from data array
const extractMetrics = (rows: any[], meta?: any): any[] => {
  if (!rows || rows.length === 0) return []

  const metrics: any[] = []

  // Add count metric
  metrics.push({
    label: 'Results',
    value: rows.length.toLocaleString(),
    rawValue: rows.length,
    key: 'count',
  })

  // Look for common numeric fields to aggregate
  const numericFields = ['total', 'revenue', 'amount', 'price', 'quantity', 'count', 'orders', 'sales', 'value']
  const firstRow = rows[0]

  if (firstRow && typeof firstRow === 'object') {
    for (const [key, value] of Object.entries(firstRow)) {
      const keyLower = key.toLowerCase()
      if (numericFields.some(f => keyLower.includes(f)) && typeof value === 'number') {
        const sum = rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0)
        metrics.push({
          label: `Total ${formatLabel(key)}`,
          value: formatValue(sum, key),
          rawValue: sum,
          key,
        })
      }
    }
  }

  // Add meta metrics if present
  if (meta && meta.total_count !== undefined) {
    metrics.push({
      label: 'Total Records',
      value: meta.total_count.toLocaleString(),
      rawValue: meta.total_count,
      key: 'total_count',
    })
  }

  return metrics.slice(0, 6)
}

// Format field label
const formatLabel = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

// Format value based on field name
const formatValue = (value: any, key: string): string => {
  if (value === null || value === undefined) return '-'

  // Handle objects - extract meaningful value
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.name !== undefined) return formatValue(value.name, key)
    if (value.value !== undefined) return formatValue(value.value, key)
    if (value.title !== undefined) return formatValue(value.title, key)
    if (value.label !== undefined) return formatValue(value.label, key)
    const objKeys = Object.keys(value)
    if (objKeys.length === 0) return '-'
    if (objKeys.length <= 2) return objKeys.map(k => `${k}: ${value[k]}`).join(', ')
    return `${objKeys.length} fields`
  }

  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) return '-'
    if (typeof value[0] === 'string' || typeof value[0] === 'number') {
      return value.slice(0, 2).join(', ') + (value.length > 2 ? '...' : '')
    }
    return `${value.length} items`
  }

  const keyLower = key.toLowerCase()
  const numValue = typeof value === 'number' ? value : Number(value)

  if (!isNaN(numValue) && typeof value !== 'string') {
    // Currency fields
    if (keyLower.includes('revenue') || keyLower.includes('amount') || keyLower.includes('price') || keyLower.includes('total') || keyLower.includes('sales') || keyLower.includes('value')) {
      return `$${numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    // Percentage fields
    if (keyLower.includes('rate') || keyLower.includes('percent') || keyLower.includes('ratio')) {
      return `${(numValue * 100).toFixed(1)}%`
    }
    // Regular numbers
    return numValue.toLocaleString()
  }

  // String truncation
  if (typeof value === 'string' && value.length > 50) {
    return value.slice(0, 47) + '...'
  }

  return String(value)
}

// Metric Card Component
const MetricCard: React.FC<{ label: string; value: string; trend?: 'up' | 'down' | 'neutral' }> = ({ label, value, trend }) => {
  const TrendIcon = trend === 'up' ? BiTrendingUp : trend === 'down' ? BiTrendingDown : BiMinus
  const trendColor = trend === 'up' ? 'tw-text-green-500' : trend === 'down' ? 'tw-text-red-500' : 'tw-text-gray-400'

  return (
    <div className="tw-bg-gradient-to-br tw-from-white tw-to-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-shadow-sm">
      <div className="tw-flex tw-items-start tw-justify-between">
        <div>
          <div className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-font-medium">{label}</div>
          <div className="tw-text-lg tw-font-bold tw-text-gray-800">{value}</div>
        </div>
        {trend && <TrendIcon className={`${trendColor} tw-mt-1`} size={18} />}
      </div>
    </div>
  )
}

// Chart Component using Highcharts
const DataChart: React.FC<{ chartData: ChartData; isExpanded: boolean }> = ({ chartData, isExpanded }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  useEffect(() => {
    // Reflow chart when panel expands/collapses
    if (chartRef.current?.chart) {
      setTimeout(() => chartRef.current?.chart?.reflow(), 300)
    }
  }, [isExpanded])

  const baseOptions: Highcharts.Options = {
    chart: {
      backgroundColor: 'transparent',
      height: chartData.type === 'pie' ? 220 : 200,
      style: { fontFamily: 'inherit' },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: {
      enabled: chartData.type === 'pie',
      itemStyle: { fontSize: '11px', fontWeight: '400', color: '#6b7280' },
    },
  }

  let options: Highcharts.Options

  if (chartData.type === 'pie') {
    options = {
      ...baseOptions,
      chart: { ...baseOptions.chart, type: 'pie' },
      plotOptions: {
        pie: {
          innerSize: '50%',
          dataLabels: {
            enabled: true,
            format: '{point.name}: {point.percentage:.1f}%',
            style: { fontSize: '10px', color: '#6b7280' },
          },
          colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'],
        },
      },
      series: chartData.series as any,
    }
  } else if (chartData.type === 'line') {
    options = {
      ...baseOptions,
      chart: { ...baseOptions.chart, type: 'spline' },
      xAxis: {
        categories: chartData.categories,
        labels: {
          style: { fontSize: '10px', color: '#9ca3af' },
          rotation: chartData.categories && chartData.categories.length > 7 ? -45 : 0,
        },
        lineColor: '#e5e7eb',
        tickColor: '#e5e7eb',
      },
      yAxis: {
        title: { text: chartData.yAxisTitle, style: { fontSize: '10px', color: '#9ca3af' } },
        gridLineColor: '#f3f4f6',
        labels: { style: { fontSize: '10px', color: '#9ca3af' } },
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
          marker: { radius: 3 },
          color: '#3b82f6',
        },
      },
      series: chartData.series as any,
    }
  } else {
    // Bar chart
    options = {
      ...baseOptions,
      chart: { ...baseOptions.chart, type: 'bar' },
      xAxis: {
        categories: chartData.categories,
        labels: {
          style: { fontSize: '10px', color: '#6b7280' },
        },
        lineColor: '#e5e7eb',
      },
      yAxis: {
        title: { text: chartData.yAxisTitle, style: { fontSize: '10px', color: '#9ca3af' } },
        gridLineColor: '#f3f4f6',
        labels: { style: { fontSize: '10px', color: '#9ca3af' } },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          color: '#3b82f6',
          dataLabels: {
            enabled: true,
            style: { fontSize: '9px', fontWeight: '400', color: '#6b7280' },
          },
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        borderColor: '#374151',
        borderRadius: 8,
        style: { color: '#f9fafb', fontSize: '11px' },
      },
      series: chartData.series as any,
    }
  }

  return (
    <div className="tw-mt-3 tw-mb-2 tw-bg-white tw-rounded-lg tw-border tw-border-gray-100 tw-p-2">
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  )
}

// Data Table Component
const DataTable: React.FC<{ rows: any[]; maxRows?: number }> = ({ rows, maxRows = 10 }) => {
  const [expanded, setExpanded] = useState(false)
  const displayRows = expanded ? rows : rows.slice(0, maxRows)

  if (!rows || rows.length === 0) {
    return <div className="tw-text-gray-400 tw-text-sm tw-py-4 tw-text-center">No data available</div>
  }

  // Get columns from first row
  const columns = Object.keys(rows[0]).filter(key => {
    const value = rows[0][key]
    return value !== null && value !== undefined && typeof value !== 'object'
  })

  if (columns.length === 0) {
    return <div className="tw-text-gray-400 tw-text-sm tw-py-4 tw-text-center">No displayable columns</div>
  }

  return (
    <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-200">
      <table className="tw-w-full tw-text-sm">
        <thead>
          <tr className="tw-bg-gray-50">
            {columns.slice(0, 6).map(col => (
              <th key={col} className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-600 tw-border-b tw-whitespace-nowrap tw-text-xs">
                {formatLabel(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, idx) => (
            <tr key={idx} className="tw-border-b tw-border-gray-100 hover:tw-bg-blue-50/50 tw-transition-colors">
              {columns.slice(0, 6).map(col => (
                <td key={col} className="tw-px-3 tw-py-2 tw-text-gray-700 tw-whitespace-nowrap tw-text-xs">
                  {formatValue(row[col], col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > maxRows && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="tw-w-full tw-text-center tw-py-2 tw-text-blue-600 hover:tw-text-blue-700 tw-text-xs tw-font-medium tw-bg-gray-50 tw-border-t tw-border-gray-100"
        >
          {expanded ? 'Show less' : `Show all ${rows.length} rows`}
        </button>
      )}
    </div>
  )
}

// Single Result Card with Chart
const ResultCard: React.FC<{
  step: AgentStep
  index: number
  isActive: boolean
  isExpanded: boolean
}> = ({ step, index, isActive, isExpanded }) => {
  const [showTable, setShowTable] = useState(false)
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart')
  const parsed = useMemo(() => parseToolOutput(step.toolOutput, step.toolInput), [step.toolOutput, step.toolInput])

  // Get query from toolInput
  const query = step.toolInput?.query || parsed.query || `Search ${index + 1}`

  // Skip charts for configuration tools - they return account metadata, not analytics data
  const isConfigTool = isConfigurationTool(step.tool)
  const hasChart = !isConfigTool && !!parsed.chartData
  const hasTable = parsed.rows.length > 0

  // Detect data source for icon
  const dataSource = detectAgentDataSource(step.tool, query, step.toolOutput)
  const SourceIcon = DataSourceIcons[dataSource] || DataSourceIcons.database
  const sourceName = getAgentDataSourceDisplayName(dataSource)

  const ChartIcon = parsed.chartData?.type === 'line' ? BiLineChart :
    parsed.chartData?.type === 'pie' ? BiPieChartAlt2 : BiBarChartAlt2

  return (
    <div className={`tw-border tw-rounded-xl tw-mb-4 tw-overflow-hidden tw-shadow-sm ${isActive ? 'tw-border-blue-300 tw-bg-blue-50/30' : 'tw-border-gray-200 tw-bg-white'}`}>
      {/* Header */}
      <div className="tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-gray-50 tw-to-white tw-border-b tw-border-gray-100">
        <div className="tw-flex tw-items-center tw-gap-2">
          {/* Data Source Icon */}
          <div
            className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded tw-bg-white tw-border tw-border-gray-200 tw-flex-shrink-0"
            title={`Source: ${sourceName}`}
          >
            <SourceIcon size={14} />
          </div>
          <span className="tw-font-medium tw-text-gray-800 tw-text-sm tw-flex-1 tw-truncate" title={query}>
            {query}
          </span>
          {/* Source badge */}
          <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-500 tw-font-medium tw-flex-shrink-0">
            {sourceName}
          </span>
          {step.status === 'running' && (
            <span className="tw-text-xs tw-text-blue-500 tw-animate-pulse tw-font-medium">Loading...</span>
          )}
          {step.status === 'completed' && (
            <span className="tw-text-green-500 tw-text-xs">✓</span>
          )}
        </div>
        {/* View Report Button */}
        {step.status === 'completed' && parsed.reportUrl && (
          <div className="tw-mt-2">
            <a
              href={parsed.reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors tw-shadow-sm"
            >
              <BiLinkExternal size={16} />
              View Report
            </a>
          </div>
        )}
      </div>

      {/* Content */}
      {step.status === 'completed' && (
        <div className="tw-p-4">
          {/* Metrics Grid */}
          {parsed.metrics.length > 0 && (
            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-2 tw-mb-4">
              {parsed.metrics.map((metric, idx) => (
                <MetricCard key={idx} label={metric.label} value={metric.value} />
              ))}
            </div>
          )}

          {/* View Toggle */}
          {(hasChart || hasTable) && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
              {hasChart && (
                <button
                  type="button"
                  onClick={() => setViewMode('chart')}
                  className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-transition-colors ${
                    viewMode === 'chart'
                      ? 'tw-bg-blue-100 tw-text-blue-700'
                      : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200'
                  }`}
                >
                  <ChartIcon size={14} />
                  Chart
                </button>
              )}
              {hasTable && (
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-transition-colors ${
                    viewMode === 'table'
                      ? 'tw-bg-blue-100 tw-text-blue-700'
                      : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200'
                  }`}
                >
                  <BiTable size={14} />
                  Table ({parsed.rows.length})
                </button>
              )}
            </div>
          )}

          {/* Chart View */}
          {viewMode === 'chart' && hasChart && parsed.chartData && (
            <DataChart chartData={parsed.chartData} isExpanded={isExpanded} />
          )}

          {/* Table View - also shown as fallback when chart mode is selected but no chart data exists */}
          {((viewMode === 'table' || (viewMode === 'chart' && !hasChart)) && hasTable) && (
            <DataTable rows={parsed.rows} />
          )}

          {/* Fallback: No data at all */}
          {!hasChart && !hasTable && (
            <div className="tw-text-gray-400 tw-text-sm tw-text-center tw-py-4 tw-bg-gray-50 tw-rounded-lg">
              No data returned
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {step.status === 'running' && (
        <div className="tw-p-6 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-gray-400">
          <div className="tw-animate-spin tw-w-5 tw-h-5 tw-border-2 tw-border-blue-300 tw-border-t-transparent tw-rounded-full"></div>
          <span className="tw-text-sm">Fetching data...</span>
        </div>
      )}
    </div>
  )
}

const AgentDataPanel: React.FC<AgentDataPanelProps> = ({
  steps,
  status,
  isExpanded,
  onToggleExpand,
  onClose,
  availableTools = [],
  onOpenScheduledTask,
  scheduledTaskShortcutVisible = false,
}) => {
  // View mode: 'cards' for individual results, 'dashboard' for unified view
  const [panelViewMode, setPanelViewMode] = useState<'cards' | 'dashboard'>('dashboard')
  // Tools section collapsed state
  const [toolsExpanded, setToolsExpanded] = useState(false)

  // Helper to check if a tool is a PayHelm MCP tool (not internal SDK tools like Read, Bash, etc.)
  const isMcpTool = (toolName: string): boolean => {
    if (!toolName) return false
    // MCP tools have prefixes like mcp__payhelm__ or payhelm__
    if (toolName.startsWith('mcp__payhelm__') || toolName.startsWith('payhelm__')) return true
    // Also allow known PayHelm tool names without prefix (for backwards compatibility)
    const knownPayhelmTools = [
      'search', 'fetch', 'get_traffic_data', 'get_traffic_metrics', 'get_traffic_summary',
      'get_ads_performance', 'get_google_ads_accounts', 'list_google_ads_client_accounts', 'get_facebook_ads_accounts', 'get_openai_ads_accounts',
      'search_google_ads', 'search_facebook_ads', 'modify_google_ads', 'modify_facebook_ads',
      'create_google_ads_pmax_campaign', 'create_google_ads_conversion', 'modify_google_ads_conversion', 'remove_google_ads_conversion',
      'get_klaviyo_accounts', 'search_klaviyo', 'get_tiktok_accounts', 'search_tiktok',
      'get_pinterest_accounts', 'search_pinterest', 'get_mailchimp_accounts', 'search_mailchimp',
      'create_mailchimp_list', 'create_mailchimp_campaign', 'update_mailchimp_campaign', 'send_mailchimp_campaign', 'replicate_mailchimp_campaign', 'delete_mailchimp_campaign',
      'create_mailchimp_member', 'add_members_to_mailchimp_list', 'tag_mailchimp_member',
      'get_mailchimp_content', 'update_mailchimp_content', 'get_mailchimp_automation_emails',
      'get_klaviyo_template', 'update_klaviyo_template', 'get_klaviyo_flow_messages',
      'get_stripe_info', 'search_stripe',
      'insure_shipment', 'void_insurance', 'update_shipping_address', 'get_current_shipping_settings',
      'export_to_email', 'export_to_csv', 'export_to_google_drive',
      'get_inventory_products', 'get_inventory_variants', 'get_inventory_summary',
      'wix_get_product', 'wix_query_products', 'wix_create_product', 'wix_edit_product', 'wix_delete_product',
      'ebay_get_inventory_item', 'ebay_get_offer', 'ebay_get_offers', 'ebay_get_listing', 'ebay_search_listings',
      'ebay_create_product', 'ebay_create_listing', 'ebay_edit_listing', 'ebay_delete_listing',
      'get_mmm_summary', 'get_mmm_channels', 'get_mmm_budget_scenarios', 'run_mmm_simulation',
      'get_orders_report', 'get_line_items_report', 'get_charts_report', 'get_trends_report',
      'get_year_comparison_report', 'get_customers_report', 'get_products_report', 'get_product_trends_report',
      'get_variants_report', 'get_brands_report', 'get_skus_report', 'get_custom_order_bases_report',
      'get_marketing_report', 'get_integrated_accounts',
    ]
    return knownPayhelmTools.includes(toolName.toLowerCase().replace('mcp__payhelm__', ''))
  }

  // Filter to only show PayHelm MCP tool steps (not internal SDK tools like Read, Bash, Glob)
  const toolSteps = useMemo(() =>
    steps.filter(s => s.tool && (s.status === 'completed' || s.status === 'running') && isMcpTool(s.tool)),
    [steps]
  )

  // Extract thoughts from non-tool steps
  const thoughts = useMemo(() =>
    steps.filter(s => !s.tool && s.thought).map(s => s.thought || ''),
    [steps]
  )

  // Set of tools that were actually called (stripped of MCP prefix, sanitized)
  const usedTools = useMemo(() => {
    const set = new Set<string>()
    for (const s of toolSteps) {
      if (s.tool) {
        // Strip MCP prefix and sanitize: only keep alphanumeric + underscores
        const cleaned = s.tool.replace(/^mcp__payhelm__/, '').replace(/[^a-zA-Z0-9_]/g, '')
        if (cleaned) set.add(cleaned)
      }
    }
    return set
  }, [toolSteps])

  // Find currently running step
  const runningStep = toolSteps.find(s => s.status === 'running')

  // Only hide panel if status is idle AND we have no steps AND this is initial state
  if (toolSteps.length === 0 && status === 'idle' && steps.length === 0) {
    return null
  }

  // Count all completed PayHelm MCP tool calls
  const completedCount = toolSteps.filter(s => s.status === 'completed').length

  // Collapsed state - thin 60px strip on the right
  if (!isExpanded) {
    return (
      <div
        className="tw-flex tw-flex-col tw-items-center tw-py-4 tw-bg-white tw-border-l tw-border-gray-200 tw-flex-shrink-0"
        style={{ width: '60px', minWidth: '60px', maxWidth: '60px', height: '100%' }}
      >
        <button
          type="button"
          onClick={onToggleExpand}
          className="tw-p-2 tw-rounded-lg tw-text-gray-500 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-colors tw-mb-2"
          title="Expand Data Explorer"
        >
          <BiData size={22} />
        </button>
        {completedCount > 0 && (
          <div className="tw-bg-blue-100 tw-text-blue-600 tw-rounded-full tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-medium tw-mb-2">
            {completedCount}
          </div>
        )}
        {status === 'running' && (
          <div className="tw-animate-spin tw-w-5 tw-h-5 tw-border-2 tw-border-blue-400 tw-border-t-transparent tw-rounded-full"></div>
        )}
        {scheduledTaskShortcutVisible && onOpenScheduledTask && (
          <button
            type="button"
            onClick={onOpenScheduledTask}
            className="tw-p-2 tw-rounded-lg tw-text-gray-500 hover:tw-text-purple-600 hover:tw-bg-purple-50 tw-transition-colors tw-mt-1"
            title="Save as scheduled task"
          >
            <BiCalendar size={22} />
          </button>
        )}
      </div>
    )
  }

  // Expanded state - takes remaining space (flex: 1) with minimum width
  return (
    <div className="tw-flex tw-flex-col tw-bg-gray-50 tw-border-l tw-border-gray-200 tw-overflow-hidden tw-flex-1" style={{ minWidth: '400px', maxWidth: '60%', height: '100%' }}>
      {/* Header */}
      <div className="tw-flex-shrink-0 tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-bg-white">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
            <BiData className="tw-text-blue-600" size={18} />
          </div>
          <div>
            <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">Data Explorer</h3>
            <span className="tw-text-xs tw-text-gray-500">
              {completedCount} queries completed
            </span>
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1">
          {/* View Mode Toggle */}
          {completedCount > 0 && (
            <div className="tw-flex tw-bg-gray-100 tw-rounded-lg tw-p-0.5 tw-mr-2">
              <button
                type="button"
                onClick={() => setPanelViewMode('dashboard')}
                className={`tw-p-1.5 tw-rounded-md tw-transition-colors ${
                  panelViewMode === 'dashboard'
                    ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm'
                    : 'tw-text-gray-500 hover:tw-text-gray-700'
                }`}
                title="Dashboard view"
              >
                <FiLayout size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPanelViewMode('cards')}
                className={`tw-p-1.5 tw-rounded-md tw-transition-colors ${
                  panelViewMode === 'cards'
                    ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm'
                    : 'tw-text-gray-500 hover:tw-text-gray-700'
                }`}
                title="Cards view"
              >
                <BiListUl size={16} />
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={onToggleExpand}
            className="tw-p-2 tw-rounded-lg tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-transition-colors"
            title="Collapse to sidebar"
          >
            <BiCollapse size={18} />
          </button>
        </div>
      </div>

      {/* Tools Section - shows used tools prominently, all tools on expand */}
      {availableTools.length > 0 && (
        <div className="tw-flex-shrink-0 tw-border-b tw-border-gray-200 tw-bg-white">
          <button
            type="button"
            onClick={() => setToolsExpanded(!toolsExpanded)}
            className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 hover:tw-bg-gray-50 tw-transition-colors"
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiWrench className="tw-text-gray-500" size={14} />
              <span className="tw-text-xs tw-font-medium tw-text-gray-600">
                {usedTools.size > 0 ? (
                  <>{usedTools.size} Tools Used <span className="tw-text-gray-400 tw-font-normal">/ {availableTools.length} available</span></>
                ) : (
                  <>{availableTools.length} Available Tools</>
                )}
              </span>
            </div>
            {toolsExpanded ? <BiChevronUp size={16} className="tw-text-gray-400" /> : <BiChevronDown size={16} className="tw-text-gray-400" />}
          </button>

          {/* Used tools - always visible when there are used tools */}
          {usedTools.size > 0 && !toolsExpanded && (
            <div className="tw-px-4 tw-pb-2">
              <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                {Array.from(usedTools).map((tool) => (
                  <ToolTooltip key={tool} tool={tool}>
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-md tw-font-medium tw-bg-blue-50 tw-text-blue-700 tw-cursor-help">
                      <FiCheckCircle size={10} className="tw-text-blue-500" />
                      {formatToolLabel(tool)}
                    </span>
                  </ToolTooltip>
                ))}
              </div>
            </div>
          )}

          {/* Expanded: show all tools grouped, used ones highlighted */}
          {toolsExpanded && (
            <div className="tw-px-4 tw-pb-3 tw-max-h-60 tw-overflow-y-auto">
              {/* Used tools section */}
              {usedTools.size > 0 && (
                <div className="tw-mb-2">
                  <div className="tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-1">Used</div>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    {Array.from(usedTools).map((tool) => (
                      <ToolTooltip key={tool} tool={tool}>
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-md tw-font-medium tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-200 tw-cursor-help">
                          <FiCheckCircle size={10} className="tw-text-blue-500" />
                          {formatToolLabel(tool)}
                        </span>
                      </ToolTooltip>
                    ))}
                  </div>
                </div>
              )}
              {/* Remaining tools */}
              <div>
                {usedTools.size > 0 && (
                  <div className="tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-1">Available</div>
                )}
                <div className="tw-flex tw-flex-wrap tw-gap-1">
                  {availableTools.filter(t => !usedTools.has(t)).map((tool, idx) => {
                    const isActionTool = tool.includes('update') || tool.includes('set') || tool.includes('enable') ||
                      tool.includes('create') || tool.includes('modify') || tool.includes('google_ads') || tool.includes('facebook_ads')
                    return (
                      <ToolTooltip key={idx} tool={tool}>
                        <span
                          className={`tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-font-mono tw-cursor-help tw-transition-colors ${
                            isActionTool
                              ? 'tw-bg-green-50 tw-text-green-600 hover:tw-bg-green-100'
                              : 'tw-bg-gray-50 tw-text-gray-400 hover:tw-bg-gray-100'
                          }`}
                        >
                          {tool}
                        </span>
                      </ToolTooltip>
                    )
                  })}
                </div>
              </div>
              <div className="tw-mt-2 tw-text-[10px] tw-text-gray-400 tw-flex tw-items-center tw-gap-3">
                <span>
                  <span className="tw-inline-block tw-w-2 tw-h-2 tw-rounded tw-bg-blue-200 tw-mr-1"></span>
                  Used in this query
                </span>
                <span>
                  <span className="tw-inline-block tw-w-2 tw-h-2 tw-rounded tw-bg-green-200 tw-mr-1"></span>
                  Action tools
                </span>
                <span className="tw-text-gray-300">|</span>
                <span>Hover for details</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content - use relative/absolute for reliable scrolling */}
      <div className="tw-flex-1 tw-relative tw-min-h-0 tw-overflow-hidden">
        <div className="tw-absolute tw-inset-0 tw-overflow-y-auto tw-overflow-x-hidden tw-p-4">
        {status === 'running' && toolSteps.length === 0 && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-text-gray-400">
            <div className="tw-animate-spin tw-w-8 tw-h-8 tw-border-3 tw-border-blue-300 tw-border-t-transparent tw-rounded-full tw-mb-3"></div>
            <span className="tw-text-sm">Waiting for data...</span>
          </div>
        )}

        {toolSteps.length === 0 && status !== 'running' && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-text-gray-500 tw-px-4">
            <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mb-4">
              <BiSearch size={32} className="tw-opacity-40" />
            </div>
            <span className="tw-text-sm tw-font-medium tw-mb-1">No data queries executed</span>
            <span className="tw-text-xs tw-text-center tw-text-gray-400">
              The agent couldn't retrieve data for this query.
            </span>
          </div>
        )}

        {/* Dashboard View - unified comprehensive view */}
        {toolSteps.length > 0 && panelViewMode === 'dashboard' && (
          <AgentResultsDashboard
            steps={steps}
            thoughts={thoughts}
            isExpanded={isExpanded}
          />
        )}

        {/* Cards View - individual result cards */}
        {toolSteps.length > 0 && panelViewMode === 'cards' && (
          <>
            {[...toolSteps].reverse().map((step, idx) => (
              <ResultCard
                key={`${step.iteration}-${step.timestamp || idx}`}
                step={step}
                index={toolSteps.length - 1 - idx}
                isActive={step === runningStep}
                isExpanded={isExpanded}
              />
            ))}
          </>
        )}
        </div>
      </div>

      {/* Footer */}
      {status === 'running' && (
        <div className="tw-flex-shrink-0 tw-px-4 tw-py-3 tw-border-t tw-border-gray-200 tw-bg-gradient-to-r tw-from-blue-50 tw-to-white">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-blue-600 tw-text-sm">
            <div className="tw-animate-spin tw-w-4 tw-h-4 tw-border-2 tw-border-blue-400 tw-border-t-transparent tw-rounded-full"></div>
            <span className="tw-font-medium">Agent is gathering data...</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgentDataPanel
